import _ from 'lodash'
import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { BaseQueryFn, FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'

import { baseApi, Tag } from './base'
import { expertiseApi } from './expertise'

import {
  CreatePresignedUrlRequest,
  CreateDocumentDto,
  Document,
  FileUrlResponse,
  FindFilesByObjectArg,
  GetFileUrlByKeyArg,
  ObjectType,
} from '@/core/models/document.model'
import { Consultation } from '@/core/models/consultation.model'
import { Expertise } from '@/core/models/expertise.model'
import { Patient } from '@/core/models/patient.model'
import { cacher } from '@/utils/rtkQueryCacheUtils'
import { getEnv } from '@/utils/helpers'

const baseURL = getEnv('VITE_API_URL')!

export const fetchPresignedUrl = async (body: CreatePresignedUrlRequest, baseQuery: (arg: Parameters<BaseQueryFn>[0]) => ReturnType<BaseQueryFn>) => {
  const result = await baseQuery({
    url: '/files/generate-presigned-url',
    method: 'POST',
    body,
  })

  return result.data ? { data: result.data as { url: string; fileName: string; key: string } } : { error: result.error as FetchBaseQueryError }
}

export const uploadToAWS = async (url: string, file: File) => {
  try {
    const headers = {
      'Content-Type': file.type,
      'Content-Encoding': 'base64',
    }
    const requestConfig: AxiosRequestConfig = {
      url,
      method: 'PUT',
      headers,
      data: file,
    }
    const response = await axios(requestConfig)
    return { data: response.data }
  } catch (error) {
    const err = error as AxiosError
    return {
      error: {
        status: err.response?.status,
        data: err.response?.data || err.message,
      } as FetchBaseQueryError,
    }
  }
}

export const fileApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    generatePresignedUrl: builder.mutation<{ url: string; fileName: string }, CreatePresignedUrlRequest>({
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        return await fetchPresignedUrl(arg, baseQuery)
      },
    }),
    uploadToAWS: builder.mutation<void, { url: string; file: File }>({
      async queryFn({ url, file }) {
        return await uploadToAWS(url, file)
      },
    }),
    addFile: builder.mutation<Document, { file: File; isRecording?: boolean; isBillingJustification?: boolean; objectType: ObjectType; objectId: string }>({
      async queryFn({ file, objectId, objectType, isRecording, isBillingJustification }, _queryApi, _extraOptions, baseQuery) {
        const presignedUrlResult = await fetchPresignedUrl(
          {
            fileName: file.name,
            fileType: file.type,
            objectType: objectType,
            objectId,
          },
          baseQuery
        )
        if (presignedUrlResult.error) return { error: presignedUrlResult.error }

        const { url, key } = presignedUrlResult.data as { url: string; key: string }
        const uploadResult = await uploadToAWS(url, file)
        if (uploadResult.error) return { error: uploadResult.error }

        const addConsultationBiologicalFileResult = await baseQuery({
          url: `/files/${objectType}/${objectId}`,
          method: 'POST',
          body: {
            file: {
              filename: file.name,
              isRecording,
              isBillingJustification,
              key: key || _.deburr(decodeURIComponent(new URL(url).pathname.slice(1))),
              objectType,
            },
          },
        })

        return addConsultationBiologicalFileResult.data
          ? { data: addConsultationBiologicalFileResult.data as Document }
          : { error: addConsultationBiologicalFileResult.error as FetchBaseQueryError }
      },
      async onQueryStarted({ objectId, objectType }, { dispatch, queryFulfilled }) {
        try {
          const { data: file } = await queryFulfilled
          switch (objectType) {
            // case 'consultation':
            //   const consultation: Consultation = yield select((state: RootState) => selectConsultationById(state, objectId))
            //   const updatedConsultation = {
            //     ...consultation,
            //     files: _.uniqBy([...consultation.files, document], 'key'),
            //   }
            //   yield put(updateConsultationSuccess(updatedConsultation))
            //   yield put(consultationUpdatedSocketEvent(updatedConsultation))
            //   break
            // case 'patient':
            //   const patient: Patient = yield select((state: RootState) => selectPatientById(state, objectId))
            //   const updatedPatient = {
            //     ...patient,
            //     files: patient?.files ? _.uniqBy([...patient.files, document], 'key') : [document],
            //   }
            //   yield put(updatePatientSuccess(updatedPatient))
            //   break
            case 'expertise':
              dispatch(
                expertiseApi.util.updateQueryData('getExpertise', objectId, (cachedExpertise) => {
                  cachedExpertise.files.push(file)
                })
              )
              break
            default:
              break
          }
        } catch { }
      },
    }),
    deleteFiles: builder.mutation<Document[], { files: Omit<Document, 'url'>[]; objectType: ObjectType; objectId: string }>({
      query: ({ files, objectId, objectType }) => ({
        url: `/files/${objectType}/${objectId}/delete`,
        method: 'POST',
        body: { files },
      }),
      async onQueryStarted({ files, objectId, objectType }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          switch (objectType) {
            // case 'consultation':
            //   const consultation: Consultation = yield select((state: RootState) => selectConsultationById(state, objectId))
            //   const updatedConsultation = {
            //     ...consultation,
            //     files: _.uniqBy([...consultation.files, document], 'key'),
            //   }
            //   yield put(updateConsultationSuccess(updatedConsultation))
            //   yield put(consultationUpdatedSocketEvent(updatedConsultation))
            //   break
            // case 'patient':
            //   const patient: Patient = yield select((state: RootState) => selectPatientById(state, objectId))
            //   const updatedPatient = {
            //     ...patient,
            //     files: patient?.files ? _.uniqBy([...patient.files, document], 'key') : [document],
            //   }
            //   yield put(updatePatientSuccess(updatedPatient))
            //   break
            case 'expertise':
              dispatch(
                expertiseApi.util.updateQueryData('getExpertise', objectId, (cachedExpertise) => {
                  cachedExpertise.files = _.reduce(
                    _.difference(_.map(cachedExpertise.files, 'key'), _.map(files, 'key')),
                    (result: Document[], key: string) => {
                      const file = _.find(cachedExpertise.files, ['key', key])
                      if (file) result.push(file)
                      return result
                    },
                    []
                  )
                })
              )
              break
            default:
              break
          }
        } catch { }
      },
    }),
    updateFilename: builder.mutation<
      Consultation | Expertise | Patient,
      { file: Omit<Document, 'url'>; newFilename: string; objectType: ObjectType; objectId: string }
    >({
      query: ({ file, objectId, objectType, newFilename }) => ({
        url: `/files/${objectType}/${objectId}/update-name`,
        method: 'PATCH',
        body: {
          file,
          update: { filename: newFilename },
        },
      }),
      async onQueryStarted({ file, objectId, objectType }, { dispatch, queryFulfilled }) {
        try {
          switch (objectType) {
            // case 'consultation':
            //   const consultation: Consultation = yield select((state: RootState) => selectConsultationById(state, objectId))
            //   const updatedConsultation = {
            //     ...consultation,
            //     files: _.uniqBy([...consultation.files, document], 'key'),
            //   }
            //   yield put(updateConsultationSuccess(updatedConsultation))
            //   yield put(consultationUpdatedSocketEvent(updatedConsultation))
            //   break
            // case 'patient':
            //   const patient: Patient = yield select((state: RootState) => selectPatientById(state, objectId))
            //   const updatedPatient = {
            //     ...patient,
            //     files: patient?.files ? _.uniqBy([...patient.files, document], 'key') : [document],
            //   }
            //   yield put(updatePatientSuccess(updatedPatient))
            //   break
            case 'expertise':
              const { data: expertise } = await queryFulfilled
              dispatch(
                expertiseApi.util.updateQueryData('getExpertise', objectId, (cachedExpertise) => {
                  Object.assign(cachedExpertise, expertise)
                })
              )
              break
            default:
              break
          }
        } catch { }
      },
    }),
    getFileUrlByKey: builder.query<FileUrlResponse, GetFileUrlByKeyArg>({
      query: ({ key }) => ({
        url: `/files/get-file-by-key`,
        method: 'POST',
        body: { key },
      }),
      providesTags: (result, error, arg) => [{ type: Tag.FILES, id: arg.key }],
    }),
    findFilesByObject: builder.query<Document[], FindFilesByObjectArg>({
      query: ({ objectId, objectType }) => ({
        url: `/files/find-files`,
        method: 'POST',
        body: { objectId, objectType },
      }),
      providesTags: (result, error, arg) => [{ type: Tag.FILES, id: `${arg.objectType}-${arg.objectId}` }],
    }),
    // Mint a single-purpose upload grant and get back a shortened magic link (for a
    // QR code). Opening it lets a patient's device upload to THIS object only — no
    // doctor session is ever issued. Replaces the former `/auth/magic-link` flow for
    // file upload.
    createUploadGrant: builder.mutation<{ url: string }, { objectType: ObjectType; objectId: string; biologicalId?: string }>({
      query: ({ objectType, objectId, biologicalId }) => ({
        url: `/files/${objectType}/${objectId}/upload-grant`,
        method: 'POST',
        body: biologicalId ? { biologicalId } : {},
      }),
    }),
    // Upload a file from the magic-link page using ONLY an upload grant. This runs
    // the presigned-url -> S3 PUT -> register flow over a bare axios instance with
    // the `X-Upload-Grant` header — never the keycloak-bound baseQuery — so no doctor
    // session/token is read, sent, or persisted, and the target object comes from the
    // grant server-side.
    uploadFileWithGrant: builder.mutation<void, { grant: string; file: File }>({
      async queryFn({ grant, file }) {
        try {
          const headers = { 'X-Upload-Grant': grant }
          const presigned = await axios.post<{ datas: { url: string; fileName: string; key: string } }>(
            `${baseURL}/file-upload/presigned-url`,
            { fileName: file.name, fileType: file.type },
            { headers }
          )
          const { url, fileName, key } = presigned.data.datas

          const uploadResult = await uploadToAWS(url, file)
          if (uploadResult.error) return { error: uploadResult.error }

          await axios.post(`${baseURL}/file-upload/file`, { filename: fileName, key, isRecording: false }, { headers })
          return { data: undefined }
        } catch (error) {
          const err = error as AxiosError
          return {
            error: {
              status: err.response?.status,
              data: err.response?.data || err.message,
            } as FetchBaseQueryError,
          }
        }
      },
    }),
  }),
  overrideExisting: false,
})
